import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import config from '../config';
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, Bar } from 'recharts';
import { Settings, Calendar } from 'lucide-react';
import { useSelectedMetrics, isMetricSelected, getCosClassName, getCpaClassName, useComponentMetrics, getMetricLabel, getCustomMetricLabel, libelleTauxConversion, libelleConversions } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { colonnesVideoGoogleAds, cumulerVideo, TOTAUX_VIDEO_INITIAUX } from '../utils/metriquesVideo';
import { getDateRangeFromPeriod, calculatePreviousPeriod } from '../utils/periodUtils';
// Endpoints API par provider
const API_ENDPOINTS = {
  google: '/api/dashboard/components/weekdays',
  microsoft: '/api/dashboard/microsoft-ads/weekdays',
  ga4: '/api/dashboard/ga4/weekdays'
};

// Configuration des métriques par provider
const PROVIDER_CONFIG = {
  google: {
    chartPrimaryMetric: 'clicks',
    chartPrimaryLabel: 'Clics',
    chartSecondaryMetric: 'conversions',
    chartSecondaryLabel: 'Conversions',
    type: 'ads'
  },
  microsoft: {
    chartPrimaryMetric: 'clicks',
    chartPrimaryLabel: 'Clics',
    chartSecondaryMetric: 'conversions',
    chartSecondaryLabel: 'Conversions',
    type: 'ads'
  },
  ga4: {
    chartPrimaryMetric: 'visites',
    chartPrimaryLabel: 'Sessions',
    chartSecondaryMetric: 'transactions',
    chartSecondaryLabel: 'Transactions',
    type: 'analytics'
  }
};

/**
 * Composant table modulaire des jours de la semaine avec graphique et tableau
 * Optimisé avec React.memo et useMemo pour les performances
 * Supporte multi-provider (Google Ads et Microsoft Ads)
 */
const WeekdaysTable = React.memo(({
  charts,
  campaignsData = [],
  isVisible = true,
  onToggleVisibility,
  formatNumber,
  selectedCampaignFilter,
  selectedCampaignTypeFilter,
  // Filtre réseau (Microsoft Ads) : appliqué côté serveur, sur les lignes brutes.
  selectedNetworkFilter = null,
  selectedAccount = null,
  COLORS = ['#0088FE', '#82ca9d', '#FFBB28', '#FF8042', '#8884d8', '#FF6B9D', '#C0CA33'],
  // Props pour la comparaison de périodes
  previousPeriodData = null,
  compareEnabled = false,
  comparisonType = 'previous_period',
  loadingComparison = false,
  // Props additionnelles pour charger les données de période précédente
  token = null,
  selectedPeriod = 'LAST_30_DAYS',
  customStartDate = null,
  customEndDate = null,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'weekdays',
  serverTotals = null,
  serverPreviousTotals = null,
  // Multi-provider support
  provider = 'google',
  // Props pour les options avancées (conversions GA4)
  propertyId = null,
  conversionConfig = null,
  onConversionConfigSave = null
}) => {
  // États locaux pour cette table
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);

  // États pour le chargement direct des données (Microsoft Ads)
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  // Refs pour debouncing et annulation de requêtes
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const lastRequestKeyRef = useRef(null);

  // Configuration des métriques pour ce composant (utilise moduleId pour supporter multi-provider)
  const componentMetrics = useComponentMetrics(moduleId, selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est sélectionnée pour ce composant
  const isComponentMetricSelected = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  // États pour les données de période précédente (chargées via API)
  const [previousWeekdaysData, setPreviousWeekdaysData] = useState([]);
  const [loadingPrevious, setLoadingPrevious] = useState(false);

  // Hook pour les métriques sélectionnées
  const selectedMetrics = useSelectedMetrics();




  // Charger les données directement via API pour Microsoft Ads et GA4 (ou utiliser charts pour Google Ads)
  useEffect(() => {
    // Pour Google Ads, utiliser les données fournies via charts
    if (provider === 'google') {
      setIsDataLoaded(true);
      return;
    }

    // Pour Microsoft et GA4, charger les données via API
    if (!isVisible || !selectedAccount?.id || !token) {
      return;
    }

    // Clé unique pour éviter les requêtes en double (inclut le filtre campagne)
    const campaignFilterId = selectedCampaignFilter?.id || selectedCampaignFilter?.campaignId || 'all';
    const requestKey = `${provider}-${selectedAccount.id}-${selectedPeriod}-${customStartDate}-${customEndDate}-${campaignFilterId}`;
    if (requestKey === lastRequestKeyRef.current && isDataLoaded) {
      return;
    }

    // Mettre loading=true immédiatement (avant le debounce) pour éviter que le composant retourne null
    setLoading(true);

    // Annuler le timer de debounce précédent
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce de 300ms
    debounceTimerRef.current = setTimeout(async () => {
      // Annuler la requête précédente si elle existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      lastRequestKeyRef.current = requestKey;

      try {
        const endpoint = API_ENDPOINTS[provider];

        // Construire le body de la requête selon le provider
        let requestBody;
        if (provider === 'ga4') {
          // GA4 utilise propertyId et des dates directes
          const { startDate, endDate } = getDateRangeFromPeriod(selectedPeriod);
          requestBody = {
            propertyId: selectedAccount.id,
            startDate: customStartDate || startDate,
            endDate: customEndDate || endDate
          };
        } else {
          // Microsoft Ads utilise sources et period
          requestBody = {
            sources: [selectedAccount.id],
            period: selectedPeriod,
            customStartDate,
            customEndDate
          };
          // Ajouter le filtre campagne si présent
          if (selectedCampaignFilter) {
            requestBody.campaignFilter = selectedCampaignFilter;
          }

          // Filtre par type de campagne : appliqué côté serveur, les données par jour
          // étant agrégées sans référence de campagne.
          if (selectedCampaignTypeFilter) {
            requestBody.campaignTypeFilter = selectedCampaignTypeFilter;
          }

          if (selectedNetworkFilter) {
            requestBody.networkFilter = selectedNetworkFilter;
          }
        }

        const response = await axios.post(`${config.API_URL}${endpoint}`, requestBody, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortControllerRef.current.signal
        });

        if (response.data?.success && response.data.data?.weekdays) {
          // Transformer les données au format attendu selon le provider
          const providerType = PROVIDER_CONFIG[provider]?.type || 'ads';

          const weekdaysData = response.data.data.weekdays.map(day => {
            if (providerType === 'analytics') {
              // Format GA4 - utiliser les clés GA4 avec transactions et conversions séparées
              const sessions = day.sessions || 0;
              const pageViews = day.pageViews || 0;
              const transactions = day.transactions || 0;
              const totalRevenue = day.totalRevenue || 0;
              const conversions = day.conversions || 0;
              return {
                name: day.name || day.weekday,
                weekday: day.weekday || day.name,
                dayNumber: day.dayNumber,
                visites: sessions,
                visiteurs: day.users || 0,
                pages_vues: pageViews,
                rebond: day.bounceRate || 0,
                pages_visite: sessions > 0 ? pageViews / sessions : 0,
                duree_moy: day.avgSessionDuration || 0,
                // E-commerce
                transactions: transactions,
                totalRevenue: totalRevenue,
                panier_moyen: transactions > 0 ? totalRevenue / transactions : 0,
                taux_transaction: sessions > 0 ? (transactions / sessions * 100) : 0,
                // Conversions GA4
                conversions: conversions,
                taux_conv: sessions > 0 ? (conversions / sessions * 100) : 0
              };
            } else {
              // Format Ads (Microsoft)
              return {
                name: day.dayOfWeek || day.name || day.weekday,
                weekday: day.dayOfWeek || day.weekday || day.name,
                dayNumber: day.dayNumber,
                impressions: day.impressions || 0,
                clicks: day.clicks || 0,
                cost: day.cost || 0,
                conversions: day.conversions || 0,
                conversionsValue: day.conversionsValue || 0,
                ctr: day.ctr || 0,
                cpc: day.cpc || 0,
                cpa: day.cpa || 0,
                cos: day.cos || 0,
                conversionRate: day.clicks > 0 ? (day.conversions / day.clicks * 100) : 0,
                // Vidéo TrueView : la route les fournit, cette recomposition les perdait.
                views: day.views || 0,
                videoImpressions: day.videoImpressions || 0,
                viewCost: day.viewCost || 0,
                vtr: day.vtr || 0,
                cpv: day.cpv || 0
              };
            }
          });

          setApiData({
            dataAggregated: weekdaysData,
            data: weekdaysData
          });
          setIsDataLoaded(true);
        }
      } catch (error) {
        if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
          console.error('[WeekdaysTable] Erreur chargement données:', error.message);
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isVisible, selectedAccount?.id, token, selectedPeriod, customStartDate, customEndDate, provider, isDataLoaded, selectedCampaignFilter, selectedCampaignTypeFilter, selectedNetworkFilter]);

  // Charger les données de la période précédente si comparaison activée
  useEffect(() => {
    if (!isVisible) return;

    if (!compareEnabled || !comparisonType || !selectedAccount?.id || !token) {
      setPreviousWeekdaysData([]);
      return;
    }

    const loadPreviousData = async () => {
      setLoadingPrevious(true);

      // Calculer les dates de la période actuelle
      let currentStartDate = customStartDate;
      let currentEndDate = customEndDate;

      // Si pas de dates custom, les calculer à partir de selectedPeriod
      if (!currentStartDate || !currentEndDate) {
        const dateRange = getDateRangeFromPeriod(selectedPeriod);
        currentStartDate = dateRange.startDate;
        currentEndDate = dateRange.endDate;
      }

      const prevPeriod = calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType);

      try {
        // Utiliser l'endpoint correct selon le provider
        const endpoint = API_ENDPOINTS[provider] || API_ENDPOINTS.google;
        const response = await axios.post(`${config.API_URL}${endpoint}`, {
          sources: [selectedAccount.id],
          period: 'CUSTOM',
          customStartDate: prevPeriod.startDate,
          customEndDate: prevPeriod.endDate,
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data?.success) {
          let prevWeekdays = [];

          // Format Microsoft Ads : response.data.data.weekdays
          if (response.data.data?.weekdays) {
            prevWeekdays = response.data.data.weekdays.map(day => ({
              name: day.dayOfWeek || day.name || day.weekday,
              weekday: day.dayOfWeek || day.weekday || day.name,
              dayNumber: day.dayNumber,
              impressions: day.impressions || 0,
              clicks: day.clicks || 0,
              cost: day.cost || 0,
              conversions: day.conversions || 0,
              conversionsValue: day.conversionsValue || 0,
              ctr: day.ctr || 0,
              cpc: day.cpc || 0,
              cpa: day.cpa || 0,
              cos: day.cos || 0
            }));
          }
          // Format Google Ads : response.data.charts
          else if (response.data.charts) {
            const weekdayChart = response.data.charts.find(chart => chart.id === 'real-weekday-conversions');
            if (weekdayChart) {
              prevWeekdays = weekdayChart.dataAggregated || [];
              if (!prevWeekdays.length && weekdayChart.data) {
                // Agréger les données par jour
                const weekdayBreakdown = {};
                weekdayChart.data.forEach(entry => {
                  const name = entry.weekday || entry.name || 'UNKNOWN';
                  const dayNumber = entry.dayNumber || 0;
                  if (!weekdayBreakdown[name]) {
                    weekdayBreakdown[name] = { name, weekday: name, dayNumber, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 };
                  }
                  weekdayBreakdown[name].impressions += entry.impressions || 0;
                  weekdayBreakdown[name].clicks += entry.clicks || 0;
                  weekdayBreakdown[name].cost += entry.cost || 0;
                  weekdayBreakdown[name].conversions += entry.conversions || 0;
                  weekdayBreakdown[name].conversionsValue += entry.conversionsValue || 0;
                });
                prevWeekdays = Object.values(weekdayBreakdown);
              }
            }
          }

          setPreviousWeekdaysData(prevWeekdays);
        }
      } catch (error) {
        console.warn(`⚠️ [WEEKDAYS] Erreur chargement période précédente:`, error.message);
        setPreviousWeekdaysData([]);
      } finally {
        setLoadingPrevious(false);
      }
    };

    loadPreviousData();
  }, [isVisible, compareEnabled, comparisonType, customStartDate, customEndDate, selectedAccount?.id, token, selectedPeriod, provider]);

  // Mapper les données de comparaison par jour de semaine (utilise les données chargées via API)
  const previousWeekdaysMap = useMemo(() => {
    if (!compareEnabled || !previousWeekdaysData.length) return {};
    const map = {};
    previousWeekdaysData.forEach(day => {
      const weekdayName = day.weekday || day.name || 'UNKNOWN';
      map[weekdayName] = day;
    });
    return map;
  }, [compareEnabled, previousWeekdaysData]);

  // Fonction pour afficher la variation avec couleur
  const renderVariation = makeRenderVariation({ fontSize: '0.8em', style: { marginTop: 0 } });

  // Libellés propres à la plateforme : GA4 dit « Sessions » là où les régies disent « Visites ».
  // Les providers publicitaires gardent leurs libellés actuels via le fallback.
  const isAnalyticsProvider = (PROVIDER_CONFIG[provider]?.type || 'ads') === 'analytics';
  const labelFor = useCallback(
    (key, fallback) => (isAnalyticsProvider ? getMetricLabel(key, moduleId, selectedAccount?.id || 'global') : fallback),
    [isAnalyticsProvider, moduleId, selectedAccount?.id]
  );

  /**
   * `conv` et `taux_conv` portent un nom qui dépend de l'événement de conversion retenu.
   *
   * `labelFor` ne convient pas pour elles : côté analytics il rend systématiquement la table
   * `GA4_METRIC_LABELS`, figée, qui ne peut pas porter la parenthèse — et qui écrase donc le
   * repli qu'on lui passe. On ne consulte ici que le nom saisi par l'utilisateur ; faute de
   * quoi on affiche le nom calculé, qui dit l'événement compté.
   */
  const labelDynamique = useCallback(
    (key, nomCalcule) => (isAnalyticsProvider
      ? (getCustomMetricLabel(key, moduleId, selectedAccount?.id || 'global') || nomCalcule)
      : getMetricLabel(key, moduleId, selectedAccount?.id || 'global')),
    [isAnalyticsProvider, moduleId, selectedAccount?.id]
  );

  /**
   * Le panier moyen se calcule sur deux jeux de champs, selon la famille du fournisseur.
   *
   * Ce tableau sert les régies (`conversions` / `conversionsValue`) **et** les outils de mesure
   * (`transactions` / `totalRevenue`). La colonne ne lisait que la seconde paire : sur Google Ads
   * comme sur Microsoft Ads, le dénominateur valait toujours zéro et le panier moyen s'affichait
   * à 0,00 € — un chiffre faux, pas une case vide, donc rien ne signalait l'erreur.
   *
   * La famille tranche, sans repli d'une paire sur l'autre : mélanger des transactions analytics
   * avec une valeur de conversion publicitaire rendrait un panier plausible et faux.
   */
  const panierMoyenDe = useCallback((ligne) => {
    if (!ligne) return 0;
    if (typeof ligne.panier_moyen === 'number' && Number.isFinite(ligne.panier_moyen)) return ligne.panier_moyen;
    const ventes = isAnalyticsProvider ? ligne.transactions : ligne.conversions;
    const revenu = isAnalyticsProvider ? ligne.totalRevenue : ligne.conversionsValue;
    return (ventes || 0) > 0 ? (revenu || 0) / ventes : 0;
  }, [isAnalyticsProvider]);

  // Configuration du rendu des métriques (utilisé pour l'ordre dynamique)
  const metricConfig = useMemo(() => ({
    impressions: {
      label: 'Impressions',
      sortKey: 'impressions',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.impressions || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(weekday.impressions || 0, prevWeekday?.impressions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.impressions)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.impressions, previousTotals.impressions))}
        </>
      )
    },
    ctr: {
      label: 'CTR',
      sortKey: 'ctr',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          {formatNumber(weekday.ctr || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(weekday.ctr || 0, prevWeekday?.ctr || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.ctr, 2)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.ctr, previousTotals.ctr))}
        </>
      )
    },
    clics: {
      label: 'Clics',
      sortKey: 'clicks',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.clicks || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(weekday.clicks || 0, prevWeekday?.clicks || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.clicks)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.clicks, previousTotals.clicks))}
        </>
      )
    },
    cpc: {
      label: 'CPC',
      sortKey: 'cpc',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.cpc || 0, 2)}€</div>
          {hasComparison && renderVariation(
            calculateVariation(weekday.cpc || 0, prevWeekday?.clicks > 0 ? (prevWeekday.cost || 0) / prevWeekday.clicks : 0),
            true
          )}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cpc, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cpc, previousTotals.cpc), true)}
        </>
      )
    },
    cout: {
      label: 'Coût',
      sortKey: 'cost',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.cost || 0, 2)}€</div>
          {hasComparison && renderVariation(calculateVariation(weekday.cost || 0, prevWeekday?.cost || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cost, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cost, previousTotals.cost), true)}
        </>
      )
    },
    taux_conv: {
      label: labelDynamique('taux_conv', libelleTauxConversion(conversionConfig)),
      sortKey: 'conversionRate',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          {formatNumber(weekday.conversionRate || 0, 2)}%
          {hasComparison && renderVariation(calculateVariation(weekday.conversionRate || 0, prevWeekday?.conversionRate || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.conversionRate, 2)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversionRate, previousTotals.conversionRate))}
        </>
      )
    },
    conv: {
      label: isAnalyticsProvider
        ? labelDynamique('conv', libelleConversions(conversionConfig))
        : 'Conv.',
      sortKey: 'conversions',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.conversions || 0, 0)}</div>
          {hasComparison && renderVariation(calculateVariation(weekday.conversions || 0, prevWeekday?.conversions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.conversions, 0)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversions, previousTotals.conversions))}
        </>
      )
    },
    // Métriques GA4 e-commerce
    transactions: {
      label: labelFor('transactions', 'Transactions'),
      sortKey: 'transactions',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.transactions || 0, 0)}</div>
          {hasComparison && renderVariation(calculateVariation(weekday.transactions || 0, prevWeekday?.transactions || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.transactions, 0)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.transactions, previousTotals.transactions))}
        </>
      )
    },
    chiffre_affaires: {
      label: labelFor('chiffre_affaires', "Chiffre d'affaires"),
      sortKey: 'totalRevenue',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.totalRevenue || 0, 2)}€</div>
          {hasComparison && renderVariation(calculateVariation(weekday.totalRevenue || 0, prevWeekday?.totalRevenue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.totalRevenue, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.totalRevenue, previousTotals.totalRevenue))}
        </>
      )
    },
    taux_transaction: {
      label: labelFor('taux_transaction', '% Conversion (transactions)'),
      sortKey: 'taux_transaction',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.taux_transaction || 0, 2)}%</div>
          {hasComparison && renderVariation(calculateVariation(weekday.taux_transaction || 0, prevWeekday?.taux_transaction || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.taux_transaction || (totals.visites > 0 ? (totals.transactions / totals.visites * 100) : 0), 2)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(
            totals.taux_transaction || (totals.visites > 0 ? (totals.transactions / totals.visites * 100) : 0),
            previousTotals.taux_transaction || (previousTotals.visites > 0 ? (previousTotals.transactions / previousTotals.visites * 100) : 0)
          ))}
        </>
      )
    },
    cpa: {
      label: 'CPA',
      sortKey: 'cpa',
      getClassName: (weekday) => getCpaClassName(weekday.cpa || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCpaClassName(totals.cpa, selectedAccount?.id || 'global', moduleId),
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.cpa || 0, 2)}€</div>
          {hasComparison && renderVariation(
            calculateVariation(weekday.cpa || 0, prevWeekday?.conversions > 0 ? (prevWeekday.cost || 0) / prevWeekday.conversions : 0),
            true
          )}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cpa, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cpa, previousTotals.cpa), true)}
        </>
      )
    },
    valeur_conv: {
      label: 'Valeur Conv.',
      sortKey: 'conversionsValue',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.conversionsValue || 0, 2)}€</div>
          {hasComparison && renderVariation(calculateVariation(weekday.conversionsValue || 0, prevWeekday?.conversionsValue || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.conversionsValue, 2)}€</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.conversionsValue, previousTotals.conversionsValue))}
        </>
      )
    },
    cos: {
      label: 'COS',
      sortKey: 'cos',
      getClassName: (weekday) => getCosClassName(weekday.cos || 0, selectedAccount?.id || 'global', moduleId),
      getTotalClassName: (totals) => getCosClassName(totals.cos, selectedAccount?.id || 'global', moduleId),
      renderCell: (weekday, prevWeekday, hasComparison) => {
        const prevCos = prevWeekday && (prevWeekday.conversionsValue || 0) > 0 ? ((prevWeekday.cost || 0) / (prevWeekday.conversionsValue || 1)) * 100 : 0;
        return (
          <>
            <div>{formatNumber(weekday.cos || 0, 1)}%</div>
            {hasComparison && renderVariation(calculateVariation(weekday.cos || 0, prevCos), true)}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.cos, 1)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.cos, previousTotals.cos), true)}
        </>
      )
    },
    panier_moyen: {
      label: labelFor('panier_moyen', 'Panier moyen'),
      sortKey: 'panier_moyen',
      renderCell: (weekday, prevWeekday, hasComparison) => {
        const panierMoyen = panierMoyenDe(weekday);
        const prevPanierMoyen = panierMoyenDe(prevWeekday);
        return (
          <>
            {formatNumber(panierMoyen, 2)}€
            {hasComparison && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const panierMoyen = panierMoyenDe(totals);
        const prevPanierMoyen = panierMoyenDe(previousTotals);
        return (
          <>
            <strong>{formatNumber(panierMoyen, 2)}€</strong>
            {previousTotals && renderVariation(calculateVariation(panierMoyen, prevPanierMoyen))}
          </>
        );
      }
    },
    roas: {
      label: 'ROAS',
      sortKey: 'roas',
      renderCell: (weekday, prevWeekday, hasComparison) => {
        const roas = (weekday.cost || 0) > 0 ? ((weekday.conversionsValue || 0) / weekday.cost) * 100 : 0;
        const prevRoas = prevWeekday && (prevWeekday.cost || 0) > 0 ? ((prevWeekday.conversionsValue || 0) / prevWeekday.cost) * 100 : 0;
        return (
          <>
            {formatNumber(roas || 0, 0)}%
            {hasComparison && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roas = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) / totals.cost) * 100 : 0;
        const prevRoas = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) / previousTotals.cost) * 100 : 0;
        return (
          <>
            <strong>{formatNumber(roas || 0, 0)}%</strong>
            {previousTotals && renderVariation(calculateVariation(roas, prevRoas))}
          </>
        );
      }
    },
    roi: {
      label: 'ROI',
      sortKey: 'roi',
      renderCell: (weekday, prevWeekday, hasComparison) => {
        const roi = (weekday.cost || 0) > 0 ? ((weekday.conversionsValue || 0) - weekday.cost) / weekday.cost : 0;
        const prevRoi = prevWeekday && (prevWeekday.cost || 0) > 0 ? ((prevWeekday.conversionsValue || 0) - prevWeekday.cost) / prevWeekday.cost : 0;
        return (
          <>
            {formatNumber(roi, 2)}
            {hasComparison && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const roi = (totals.cost || 0) > 0 ? ((totals.conversionsValue || 0) - totals.cost) / totals.cost : 0;
        const prevRoi = previousTotals && (previousTotals.cost || 0) > 0 ? ((previousTotals.conversionsValue || 0) - previousTotals.cost) / previousTotals.cost : 0;
        return (
          <>
            <strong>{formatNumber(roi, 2)}</strong>
            {previousTotals && renderVariation(calculateVariation(roi, prevRoi))}
          </>
        );
      }
    },
    // Métriques GA4 / Analytics (clés standard)
    visites: {
      label: labelFor('visites', 'Visites'),
      sortKey: 'visites',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.visites || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(weekday.visites || 0, prevWeekday?.visites || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.visites)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.visites, previousTotals.visites))}
        </>
      )
    },
    visiteurs: {
      label: labelFor('visiteurs', 'Visiteurs'),
      sortKey: 'visiteurs',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.visiteurs || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(weekday.visiteurs || 0, prevWeekday?.visiteurs || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.visiteurs)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.visiteurs, previousTotals.visiteurs))}
        </>
      )
    },
    rebond: {
      label: labelFor('rebond', '% Rebond'),
      sortKey: 'rebond',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.rebond || 0, 1)}%</div>
          {hasComparison && renderVariation(calculateVariation(weekday.rebond || 0, prevWeekday?.rebond || 0), true)}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.rebond, 1)}%</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.rebond, previousTotals.rebond), true)}
        </>
      )
    },
    pages_vues: {
      label: labelFor('pages_vues', 'Pages vues'),
      sortKey: 'pages_vues',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          <div>{formatNumber(weekday.pages_vues || 0)}</div>
          {hasComparison && renderVariation(calculateVariation(weekday.pages_vues || 0, prevWeekday?.pages_vues || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.pages_vues)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.pages_vues, previousTotals.pages_vues))}
        </>
      )
    },
    pages_visite: {
      label: labelFor('pages_visite', 'Pages/Visite'),
      sortKey: 'pages_visite',
      renderCell: (weekday, prevWeekday, hasComparison) => (
        <>
          {formatNumber(weekday.pages_visite || 0, 2)}
          {hasComparison && renderVariation(calculateVariation(weekday.pages_visite || 0, prevWeekday?.pages_visite || 0))}
        </>
      ),
      renderTotal: (totals, previousTotals) => (
        <>
          <div><strong>{formatNumber(totals.pages_visite, 2)}</strong></div>
          {previousTotals && renderVariation(calculateVariation(totals.pages_visite, previousTotals.pages_visite))}
        </>
      )
    },
    duree_moy: {
      label: labelFor('duree_moy', 'Durée moy.'),
      sortKey: 'duree_moy',
      renderCell: (weekday, prevWeekday, hasComparison) => {
        const seconds = weekday.duree_moy || 0;
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return (
          <>
            {mins}:{secs.toString().padStart(2, '0')}
            {hasComparison && renderVariation(calculateVariation(seconds, prevWeekday?.duree_moy || 0))}
          </>
        );
      },
      renderTotal: (totals, previousTotals) => {
        const seconds = totals.duree_moy || 0;
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return (
          <>
            <div><strong>{mins}:{secs.toString().padStart(2, '0')}</strong></div>
            {previousTotals && renderVariation(calculateVariation(totals.duree_moy, previousTotals.duree_moy))}
          </>
        );
      }
    },
    ...colonnesVideoGoogleAds({ formatNumber, renderVariation, calculateVariation })
  }), [formatNumber, selectedAccount?.id, renderVariation, calculateVariation, labelFor, labelDynamique, isAnalyticsProvider, conversionConfig, panierMoyenDe]);

  // Extraction des données de jours de la semaine (memoized)
  const weekdayChart = useMemo(() => {
    // Pour Microsoft et GA4, utiliser les données chargées via API
    if ((provider === 'microsoft' || provider === 'ga4') && apiData) {
      return {
        id: `${provider}-weekdays`,
        dataAggregated: apiData.dataAggregated,
        data: apiData.data
      };
    }
    // Pour Google Ads, utiliser les données fournies via charts
    if (!charts || !Array.isArray(charts)) return null;
    return charts.find(chart => chart.id === 'real-weekday-conversions');
  }, [charts, provider, apiData]);

  const weekdayData = useMemo(() => {
      
    if (!weekdayChart) {
      return [];
    }
    
    // Debugging conversions
    if (weekdayChart.dataAggregated && weekdayChart.dataAggregated.length > 0) {
    }
    
    // Utiliser les données du weekdayChart
    let allWeekdayData = weekdayChart.dataAggregated || weekdayChart.data || [];
    
    
    // Vérifier s'il y a des données
    if (!allWeekdayData || !Array.isArray(allWeekdayData) || allWeekdayData.length === 0) {
      return [];
    }
    
    // Les données sont déjà agrégées par le backend
    
    // Normaliser les données pour s'assurer que 'name' existe
    if (allWeekdayData && allWeekdayData.length > 0) {
      allWeekdayData = allWeekdayData.map(weekday => ({
        ...weekday,
        name: weekday.name || weekday.weekday || 'Jour inconnu' // Assurer que 'name' existe
      }));
      
    }
    
    // Si pas de données agrégées mais des données individuelles, agréger toutes les campagnes
    if ((!weekdayChart.dataAggregated || weekdayChart.dataAggregated.length === 0) && weekdayChart.data && weekdayChart.data.length > 0) {
      const aggregatedData = {};
      
      // Agréger toutes les entrées par jour de la semaine
      weekdayChart.data.forEach(weekdayEntry => {
        const dayName = weekdayEntry.weekday || weekdayEntry.name;
        const dayNumber = weekdayEntry.dayNumber;
        
        if (!aggregatedData[dayName]) {
          aggregatedData[dayName] = {
            name: dayName,
            weekday: dayName,
            dayNumber: dayNumber,
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            value: 0,
            conversionsValue: 0
          };
        }
        
        const weekday = aggregatedData[dayName];
        weekday.impressions += weekdayEntry.impressions || 0;
        weekday.clicks += weekdayEntry.clicks || 0;
        weekday.cost += weekdayEntry.cost || 0;
        weekday.conversions += weekdayEntry.conversions || 0;
        weekday.value += weekdayEntry.conversions || 0; // Pour le graphique
        weekday.conversionsValue += weekdayEntry.conversionsValue || 0;
      });
      
      // Convertir en tableau et calculer les métriques dérivées
      allWeekdayData = Object.values(aggregatedData).map(weekday => ({
        ...weekday,
        ctr: weekday.impressions > 0 ? (weekday.clicks / weekday.impressions * 100) : 0,
        cpc: weekday.clicks > 0 ? (weekday.cost / weekday.clicks) : 0,
        conversionRate: weekday.clicks > 0 ? (weekday.conversions / weekday.clicks * 100) : 0,
        cpa: weekday.conversions > 0 ? (weekday.cost / weekday.conversions) : 0,
        cos: weekday.conversionsValue > 0 ? (weekday.cost / weekday.conversionsValue * 100) : 0
      }));
      
    }
    
    // Appliquer le filtre par campagne si une campagne est sélectionnée
    if (selectedCampaignFilter && weekdayChart.data) {
      // Vérifier si les données ont déjà été filtrées par le backend (pas de campaignId dans les entrées)
      const firstEntry = weekdayChart.data[0];
      const hasNoCampaignId = firstEntry && !firstEntry.campaignId && !firstEntry.campaign_id;

      if (hasNoCampaignId) {
        // Les données sont déjà filtrées par le backend, utiliser directement
        allWeekdayData = weekdayChart.dataAggregated || weekdayChart.data || [];
        // S'assurer que les métriques dérivées sont calculées
        allWeekdayData = allWeekdayData.map(weekday => ({
          ...weekday,
          name: weekday.name || weekday.weekday || 'Jour inconnu',
          ctr: weekday.ctr || (weekday.impressions > 0 ? (weekday.clicks / weekday.impressions * 100) : 0),
          cpc: weekday.cpc || (weekday.clicks > 0 ? (weekday.cost / weekday.clicks) : 0),
          conversionRate: weekday.conversionRate || (weekday.clicks > 0 ? (weekday.conversions / weekday.clicks * 100) : 0),
          cpa: weekday.cpa || (weekday.conversions > 0 ? (weekday.cost / weekday.conversions) : 0),
          cos: weekday.cos || (weekday.conversionsValue > 0 ? (weekday.cost / weekday.conversionsValue * 100) : 0)
        }));
      } else {
        // Utiliser les données individuelles pour le filtrage (ancien comportement)
        const filteredWeekdayData = {};

        weekdayChart.data.forEach(weekdayEntry => {
          // Vérifier si cette entrée correspond à la campagne filtrée
          const matchesCampaign = weekdayEntry.campaignId === selectedCampaignFilter.id ||
                                 weekdayEntry.campaignId === selectedCampaignFilter.campaignId ||
                                 weekdayEntry.campaignId?.toString() === selectedCampaignFilter.id?.toString() ||
                                 weekdayEntry.campaignName === selectedCampaignFilter.name;

          if (matchesCampaign) {
            const dayName = weekdayEntry.weekday || weekdayEntry.name;
            const dayNumber = weekdayEntry.dayNumber;

            if (!filteredWeekdayData[dayName]) {
              filteredWeekdayData[dayName] = {
                name: dayName,
                weekday: dayName,
                dayNumber: dayNumber,
                impressions: 0,
                clicks: 0,
                cost: 0,
                conversions: 0,
                value: 0, // Pour le graphique
                conversionsValue: 0
              };
            }

            const weekday = filteredWeekdayData[dayName];
            weekday.impressions += weekdayEntry.impressions || 0;
            weekday.clicks += weekdayEntry.clicks || 0;
            weekday.cost += weekdayEntry.cost || 0;
            weekday.conversions += weekdayEntry.conversions || 0;
            weekday.value += weekdayEntry.conversions || 0; // Pour le graphique
            weekday.conversionsValue += weekdayEntry.conversionsValue || 0;
          }
        });

        // Convertir en tableau et calculer les métriques dérivées
        allWeekdayData = Object.values(filteredWeekdayData).map(weekday => ({
          ...weekday,
          ctr: weekday.impressions > 0 ? (weekday.clicks / weekday.impressions * 100) : 0,
          cpc: weekday.clicks > 0 ? (weekday.cost / weekday.clicks) : 0,
          conversionRate: weekday.clicks > 0 ? (weekday.conversions / weekday.clicks * 100) : 0,
          cpa: weekday.conversions > 0 ? (weekday.cost / weekday.conversions) : 0,
          cos: weekday.conversionsValue > 0 ? (weekday.cost / weekday.conversionsValue * 100) : 0
        }));
      }

    }
    
    // Appliquer le filtre par type de campagne si un type est sélectionné
    if (selectedCampaignTypeFilter && weekdayChart && weekdayChart.data) {
      // Vérifier si les données ont déjà été filtrées par le backend (pas de campaignId dans les entrées)
      const firstEntry = weekdayChart.data[0];
      const hasNoCampaignId = firstEntry && !firstEntry.campaignId && !firstEntry.campaign_id;

      if (hasNoCampaignId) {
        // Les données sont déjà filtrées par le backend, utiliser directement
        allWeekdayData = weekdayChart.dataAggregated || weekdayChart.data || [];
        // S'assurer que les métriques dérivées sont calculées
        allWeekdayData = allWeekdayData.map(weekday => ({
          ...weekday,
          name: weekday.name || weekday.weekday || 'Jour inconnu',
          ctr: weekday.ctr || (weekday.impressions > 0 ? (weekday.clicks / weekday.impressions * 100) : 0),
          cpc: weekday.cpc || (weekday.clicks > 0 ? (weekday.cost / weekday.clicks) : 0),
          conversionRate: weekday.conversionRate || (weekday.clicks > 0 ? (weekday.conversions / weekday.clicks * 100) : 0),
          cpa: weekday.cpa || (weekday.conversions > 0 ? (weekday.cost / weekday.conversions) : 0),
          cos: weekday.cos || (weekday.conversionsValue > 0 ? (weekday.cost / weekday.conversionsValue * 100) : 0)
        }));
      } else {
        // Filtrage par type de campagne côté frontend
        // Trouver les IDs des campagnes qui correspondent au type sélectionné
        const matchingCampaignIds = campaignsData
          .filter(campaign => {
            const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
            const matches = campaignType === selectedCampaignTypeFilter.type ||
                           String(campaignType) === String(selectedCampaignTypeFilter.type) ||
                           campaignType === selectedCampaignTypeFilter.name ||
                           String(campaignType) === String(selectedCampaignTypeFilter.name);

            return matches;
          })
          .map(campaign => campaign.id || campaign.campaignId);

        // Utiliser les données individuelles pour le filtrage par type de campagne
        const filteredWeekdayData = {};

        weekdayChart.data.forEach((weekdayEntry, index) => {
          // Vérifier si cette entrée appartient à une campagne du type filtré
          const weekdayCampaignId = weekdayEntry.campaignId || weekdayEntry.campaign_id;

          const matchesCampaignType = matchingCampaignIds.includes(weekdayCampaignId) ||
                                     matchingCampaignIds.includes(String(weekdayCampaignId)) ||
                                     matchingCampaignIds.includes(Number(weekdayCampaignId)) ||
                                     matchingCampaignIds.some(id => String(id) === String(weekdayCampaignId));

          if (matchesCampaignType) {
            const dayName = weekdayEntry.weekday || weekdayEntry.name;
            const dayNumber = weekdayEntry.dayNumber;

            if (!filteredWeekdayData[dayName]) {
              filteredWeekdayData[dayName] = {
                name: dayName,
                weekday: dayName,
                dayNumber: dayNumber,
                impressions: 0,
                clicks: 0,
                cost: 0,
                conversions: 0,
                value: 0,
                conversionsValue: 0
              };
            }

            const weekday = filteredWeekdayData[dayName];
            weekday.impressions += weekdayEntry.impressions || 0;
            weekday.clicks += weekdayEntry.clicks || 0;
            weekday.cost += weekdayEntry.cost || 0;
            weekday.conversions += weekdayEntry.conversions || 0;
            weekday.value += weekdayEntry.conversions || 0; // Pour le graphique
            weekday.conversionsValue += weekdayEntry.conversionsValue || 0;
          }
        });

        // Convertir en tableau et calculer les métriques dérivées
        allWeekdayData = Object.values(filteredWeekdayData).map(weekday => ({
          ...weekday,
          ctr: weekday.impressions > 0 ? (weekday.clicks / weekday.impressions * 100) : 0,
          cpc: weekday.clicks > 0 ? (weekday.cost / weekday.clicks) : 0,
          conversionRate: weekday.clicks > 0 ? (weekday.conversions / weekday.clicks * 100) : 0,
          cpa: weekday.conversions > 0 ? (weekday.cost / weekday.conversions) : 0,
          cos: weekday.conversionsValue > 0 ? (weekday.cost / weekday.conversionsValue * 100) : 0
        }));
      }

    }
    
    
    return allWeekdayData;
  }, [weekdayChart, selectedCampaignFilter, selectedCampaignTypeFilter, campaignsData]);


  // Fonction de tri memoized (spéciale pour les jours de la semaine)
  const sortedWeekdays = useMemo(() => {
    if (!weekdayData.length) {
      return [];
    }

    if (sortConfig.key === 'name') {
      // Tri par ordre des jours de la semaine (lundi à dimanche)
      const dayOrder = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
      return [...weekdayData].sort((a, b) => {
        const aIndex = dayOrder.indexOf(a.name);
        const bIndex = dayOrder.indexOf(b.name);
        return sortConfig.direction === 'asc' ? aIndex - bIndex : bIndex - aIndex;
      });
    }
    
    return [...weekdayData].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Pour les valeurs numériques
      if (typeof aValue === 'string' && !isNaN(aValue)) aValue = parseFloat(aValue);
      if (typeof bValue === 'string' && !isNaN(bValue)) bValue = parseFloat(bValue);
      
      aValue = Number(aValue) || 0;
      bValue = Number(bValue) || 0;
      
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [weekdayData, sortConfig]);

  // Données pour le graphique triées par ordre des jours (memoized)
  const chartData = useMemo(() => {
    
    // Utiliser les données agrégées pour le graphique si disponibles ET si aucun filtre n'est appliqué
    const dataToUse = weekdayChart?.dataAggregated && !selectedCampaignFilter && !selectedCampaignTypeFilter ? 
      weekdayChart.dataAggregated : weekdayData;
    
    
    if (!dataToUse.length) return [];
    
    const dayOrder = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    const sorted = [...dataToUse].sort((a, b) => {
      const aIndex = dayOrder.indexOf(a.weekday || a.name);
      const bIndex = dayOrder.indexOf(b.weekday || b.name);
      return aIndex - bIndex;
    });
    
    
    return sorted;
  }, [weekdayChart, weekdayData, selectedCampaignFilter, selectedCampaignTypeFilter]);

  // Calcul des totaux memoized
  const totals = useMemo(() => {
    if (!weekdayData.length) return {};

    const providerType = PROVIDER_CONFIG[provider]?.type || 'ads';

    if (providerType === 'analytics') {
      // Le total d'une période ne dépend pas de la dimension : on affiche celui que GA4 a
      // calculé pour l'ensemble du site, pas la somme des sept jours de la semaine.
      if (serverTotals && serverTotals.visites > 0) return serverTotals;

      // Totaux pour GA4 - avec transactions et conversions séparées
      const baseTotals = weekdayData.reduce((acc, weekday) => ({
        visites: acc.visites + (weekday.visites || 0),
        visiteurs: acc.visiteurs + (weekday.visiteurs || 0),
        pages_vues: acc.pages_vues + (weekday.pages_vues || 0),
        transactions: acc.transactions + (weekday.transactions || 0),
        totalRevenue: acc.totalRevenue + (weekday.totalRevenue || 0),
        conversions: acc.conversions + (weekday.conversions || 0),
        rebondSum: acc.rebondSum + ((weekday.rebond || 0) * (weekday.visites || 0)),
        dureeSum: acc.dureeSum + ((weekday.duree_moy || 0) * (weekday.visites || 0))
      }), { visites: 0, visiteurs: 0, pages_vues: 0, transactions: 0, totalRevenue: 0, conversions: 0, rebondSum: 0, dureeSum: 0 });

      return {
        ...baseTotals,
        rebond: baseTotals.visites > 0 ? baseTotals.rebondSum / baseTotals.visites : 0,
        pages_visite: baseTotals.visites > 0 ? baseTotals.pages_vues / baseTotals.visites : 0,
        duree_moy: baseTotals.visites > 0 ? baseTotals.dureeSum / baseTotals.visites : 0,
        taux_transaction: baseTotals.visites > 0 ? (baseTotals.transactions / baseTotals.visites * 100) : 0,
        taux_conv: baseTotals.visites > 0 ? (baseTotals.conversions / baseTotals.visites * 100) : 0,
        panier_moyen: baseTotals.transactions > 0 ? baseTotals.totalRevenue / baseTotals.transactions : 0
      };
    } else {
      // Totaux pour Ads
      const baseTotals = weekdayData.reduce((acc, weekday) => ({
        impressions: acc.impressions + (weekday.impressions || 0),
        clicks: acc.clicks + (weekday.clicks || 0),
        cost: acc.cost + (weekday.cost || 0),
        conversions: acc.conversions + (weekday.conversions || 0),
        conversionsValue: acc.conversionsValue + (weekday.conversionsValue || 0),
        ...cumulerVideo(acc, weekday)
      }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

      return {
        ...baseTotals,
        ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
        cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
        conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
        cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
        cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
      };
    }
  }, [weekdayData, provider, serverTotals]);

  // Calcul des totaux de la période précédente memoized
  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousWeekdaysData.length) return {};

    const providerType = PROVIDER_CONFIG[provider]?.type || 'ads';

    if (providerType === 'analytics') {
      // Le total d'une période ne dépend pas de la dimension : on affiche celui que GA4 a
      // calculé pour l'ensemble du site, pas la somme des sept jours de la semaine.
      if (serverPreviousTotals && serverPreviousTotals.visites > 0) return serverPreviousTotals;

      // Totaux pour GA4 - avec transactions et conversions séparées
      const baseTotals = previousWeekdaysData.reduce((acc, weekday) => ({
        visites: acc.visites + (weekday.visites || 0),
        visiteurs: acc.visiteurs + (weekday.visiteurs || 0),
        pages_vues: acc.pages_vues + (weekday.pages_vues || 0),
        transactions: acc.transactions + (weekday.transactions || 0),
        totalRevenue: acc.totalRevenue + (weekday.totalRevenue || 0),
        conversions: acc.conversions + (weekday.conversions || 0),
        rebondSum: acc.rebondSum + ((weekday.rebond || 0) * (weekday.visites || 0)),
        dureeSum: acc.dureeSum + ((weekday.duree_moy || 0) * (weekday.visites || 0))
      }), { visites: 0, visiteurs: 0, pages_vues: 0, transactions: 0, totalRevenue: 0, conversions: 0, rebondSum: 0, dureeSum: 0 });

      return {
        ...baseTotals,
        rebond: baseTotals.visites > 0 ? baseTotals.rebondSum / baseTotals.visites : 0,
        pages_visite: baseTotals.visites > 0 ? baseTotals.pages_vues / baseTotals.visites : 0,
        duree_moy: baseTotals.visites > 0 ? baseTotals.dureeSum / baseTotals.visites : 0,
        taux_transaction: baseTotals.visites > 0 ? (baseTotals.transactions / baseTotals.visites * 100) : 0,
        taux_conv: baseTotals.visites > 0 ? (baseTotals.conversions / baseTotals.visites * 100) : 0,
        panier_moyen: baseTotals.transactions > 0 ? baseTotals.totalRevenue / baseTotals.transactions : 0
      };
    } else {
      // Totaux pour Ads
      const baseTotals = previousWeekdaysData.reduce((acc, weekday) => ({
        impressions: acc.impressions + (weekday.impressions || 0),
        clicks: acc.clicks + (weekday.clicks || 0),
        cost: acc.cost + (weekday.cost || 0),
        conversions: acc.conversions + (weekday.conversions || 0),
        conversionsValue: acc.conversionsValue + (weekday.conversionsValue || 0),
        ...cumulerVideo(acc, weekday)
      }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ...TOTAUX_VIDEO_INITIAUX });

      return {
        ...baseTotals,
        ctr: baseTotals.impressions > 0 ? (baseTotals.clicks / baseTotals.impressions * 100) : 0,
        cpc: baseTotals.clicks > 0 ? (baseTotals.cost / baseTotals.clicks) : 0,
        conversionRate: baseTotals.clicks > 0 ? (baseTotals.conversions / baseTotals.clicks * 100) : 0,
        cpa: baseTotals.conversions > 0 ? (baseTotals.cost / baseTotals.conversions) : 0,
        cos: baseTotals.conversionsValue > 0 ? (baseTotals.cost / baseTotals.conversionsValue * 100) : 0
      };
    }
  }, [compareEnabled, previousWeekdaysData, provider, serverPreviousTotals]);

  // Handler de tri memoized
  const handleSort = useCallback((key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Pour Microsoft et GA4, afficher le loading pendant le chargement
  if ((provider === 'microsoft' || provider === 'ga4') && loading) {
    return (
      <div className="device-conversions-section">
        <div className="section-header">
          <h4><Calendar size={16} />&nbsp;Jours de la semaine</h4>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: '#6b7280' }}>
          Chargement des données...
        </div>
      </div>
    );
  }

  // Ne pas rendre si pas de données (sauf pendant le chargement)
  if (!weekdayData.length && !loading) {
    return null;
  }

  // Pour les clients : masquer complètement si le module est fermé
  if (userRole === 'CLIENT' && !isVisible) {
    return null;
  }

  return (
    <div className="device-conversions-section">
      <div className="section-header">
        <h4><Calendar size={16} />&nbsp;Jours de la semaine</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Bouton configuration des métriques - visible uniquement pour non-clients */}
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Jours de la semaine"
            />
          ) : (
            <button
              onClick={onToggleVisibility}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Indicateur de chargement des données de comparaison */}
          {compareEnabled && (loadingComparison || loadingPrevious) && (
            <div className="loading-message" style={{ textAlign: 'center', padding: '20px' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
              Chargement des données de comparaison...
            </div>
          )}
          {/* Graphique combiné barres + ligne */}
          {(!compareEnabled || (!loadingComparison && !loadingPrevious)) && (
          <>
          <div
            style={{
              width: '100%',
              height: '400px',
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxSizing: 'border-box',
              overflow: 'hidden',
              marginBottom: '20px'
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="weekday"
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                  height={40}
                />
                <YAxis
                  yAxisId="left"
                  stroke="#82ca9d"
                  tick={{ fontSize: 12 }}
                  label={{ value: PROVIDER_CONFIG[provider]?.chartPrimaryLabel || 'Clics', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#8884d8"
                  tick={{ fontSize: 12 }}
                  label={{ value: PROVIDER_CONFIG[provider]?.chartSecondaryLabel || 'Conversions', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  cursor={{ fill: '#f9fafb' }}
                />
                <Legend />
                <Bar
                  yAxisId="left"
                  dataKey={PROVIDER_CONFIG[provider]?.chartPrimaryMetric || 'clicks'}
                  fill="#82ca9d"
                  fillOpacity={0.7}
                  name={PROVIDER_CONFIG[provider]?.chartPrimaryLabel || 'Clics'}
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="right"
                  dataKey={PROVIDER_CONFIG[provider]?.chartSecondaryMetric || 'conversions'}
                  fill="#8884d8"
                  fillOpacity={0.8}
                  name={PROVIDER_CONFIG[provider]?.chartSecondaryLabel || 'Conversions'}
                  radius={[4, 4, 0, 0]}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Tableau détaillé */}
          <div className="weekday-table-wrapper" style={{ marginTop: '20px' }}>
            <div className="device-summary-table" style={{ marginTop: '0', paddingTop: '0' }}>
              <table 
                key={selectedMetrics.filter(m => m.selected).map(m => m.key).join(',')}
                className="devices-table"
                style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}
              >
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="sortable">
                      Jour {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                    </th>
                    {/* Rendu dynamique des colonnes selon l'ordre configuré */}
                    {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                      const config = metricConfig[metricKey];
                      return (
                        <th key={metricKey} onClick={() => handleSort(config.sortKey)} className="sortable numeric">
                          <span className="th-libelle">{config.label}</span> {sortConfig.key === config.sortKey && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody>
                  {sortedWeekdays.map((weekday, index) => {
                    const weekdayName = weekday.name || weekday.weekday || 'Jour inconnu';
                    const prevWeekday = previousWeekdaysMap[weekdayName] || {};
                    const hasComparison = compareEnabled && Object.keys(prevWeekday).length > 0;

                    return (
                      <tr key={index}>
                        <td className="device-name">
                          <span
                            className="device-indicator"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></span>
                          {weekdayName}
                        </td>
                        {/* Rendu dynamique des cellules selon l'ordre configuré */}
                        {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                          const config = metricConfig[metricKey];
                          const className = config.getClassName ? `numeric ${config.getClassName(weekday)}` : 'numeric';
                          return (
                            <td key={metricKey} className={className} style={{ verticalAlign: 'top' }}>
                              {config.renderCell(weekday, prevWeekday, hasComparison)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>

                <tfoot>
                  {(() => {
                    const hasFooterComparison = compareEnabled && Object.keys(previousTotals).length > 0;
                    return (
                      <tr className="total-row">
                        <td className="device-name"><strong>TOTAL</strong></td>
                        {/* Rendu dynamique des totaux selon l'ordre configuré */}
                        {componentMetrics.order.filter(key => isComponentMetricSelected(key) && metricConfig[key]).map(metricKey => {
                          const config = metricConfig[metricKey];
                          const className = config.getTotalClassName ? `numeric ${config.getTotalClassName(totals)}` : 'numeric';
                          return (
                            <td key={metricKey} className={className} style={{ verticalAlign: 'top' }}>
                              {config.renderTotal(totals, hasFooterComparison ? previousTotals : null)}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          </div>
          </>
          )}
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId={moduleId}
        accountId={selectedAccount?.id || 'global'}
        componentTitle="Jours de la semaine"
        token={token}
        propertyId={propertyId}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={onConversionConfigSave}
      />
    </div>
  );
});

WeekdaysTable.displayName = 'WeekdaysTable';

export default WeekdaysTable;